import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ProductCard from '@/components/ProductCard';
import { Skeleton } from '@/components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
    { key: 'all',         label: 'All',         categories: null },
    { key: 'apparel',     label: 'Apparel',     categories: ['hoodies', 'tees'] },
    { key: 'swimwear',    label: 'Swimwear',    categories: ['swimwear', 'shorts'] },
    { key: 'accessories', label: 'Accessories', categories: ['accessories'] },
];

export default function MerchPage() {
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState('all');

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await axios.get(`${API}/products`);
                setProducts(response.data);
            } catch (error) {
                console.error('Error fetching products:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchProducts();
    }, []);

    const filteredProducts = useMemo(() => {
        const filter = FILTERS.find((f) => f.key === activeFilter);
        if (!filter || !filter.categories) return products;
        return products.filter((p) => filter.categories.includes(p.category));
    }, [products, activeFilter]);

    return (
        <div data-testid="merch-page" className="min-h-screen bg-brand-bg pt-24">
            {/* Header */}
            <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    <p className="font-mono text-xs text-brand-primary uppercase tracking-widest mb-4">OFFICIAL STORE</p>
                    <h1 className="font-syne font-extrabold text-5xl md:text-7xl text-white tracking-tighter uppercase mb-6">
                        MERCH
                    </h1>
                    <p className="font-space text-slate-400 text-lg max-w-2xl">
                        Exclusive StillFaded apparel and accessories. Limited drops, premium quality.
                    </p>
                </div>
            </section>

            {/* Lookbook Reel */}
            <section className="px-6 md:px-12 lg:px-24 mb-12">
                <div className="max-w-7xl mx-auto">
                    <div
                        data-testid="lookbook-reel"
                        className="relative w-full aspect-[21/9] md:aspect-[21/7] overflow-hidden bg-brand-bg border border-white/5"
                    >
                        {/* Blurred backdrop fills wide banner */}
                        <img
                            src="https://customer-assets.emergentagent.com/job_faede058-fdbe-47e4-91fd-85dc70d71f6f/artifacts/5c6k4l9o_7496734D-1048-4606-B339-A7672447C3C2.jpeg"
                            alt=""
                            aria-hidden="true"
                            className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-30"
                        />
                        {/* Centered vertical video */}
                        <video
                            src={`${process.env.REACT_APP_BACKEND_URL}/api/static/lookbook/summer26.mp4`}
                            poster="https://customer-assets.emergentagent.com/job_faede058-fdbe-47e4-91fd-85dc70d71f6f/artifacts/5c6k4l9o_7496734D-1048-4606-B339-A7672447C3C2.jpeg"
                            autoPlay
                            loop
                            muted
                            playsInline
                            preload="auto"
                            className="relative w-full h-full object-contain"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-brand-bg/60 via-transparent to-brand-bg/60 pointer-events-none" />
                        <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6 z-10">
                            <p className="font-mono text-[10px] md:text-xs text-brand-primary uppercase tracking-widest mb-1">SUMMER &apos;26 LOOKBOOK</p>
                            <h2 className="font-syne font-extrabold text-2xl md:text-4xl text-white tracking-tight uppercase">
                                STAY FADED IN THE SUN
                            </h2>
                        </div>
                    </div>
                </div>
            </section>

            {/* Products Grid */}
            <section className="pb-24 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    {/* Category chip filter */}
                    <div
                        data-testid="category-filter"
                        className="flex flex-wrap gap-3 mb-10"
                    >
                        {FILTERS.map((f) => {
                            const isActive = activeFilter === f.key;
                            const count = f.categories
                                ? products.filter((p) => f.categories.includes(p.category)).length
                                : products.length;
                            return (
                                <button
                                    key={f.key}
                                    data-testid={`filter-${f.key}`}
                                    onClick={() => setActiveFilter(f.key)}
                                    className={`px-5 py-2 font-mono text-xs uppercase tracking-widest border rounded-full transition-colors duration-300 ${
                                        isActive
                                            ? 'bg-white text-black border-white'
                                            : 'bg-transparent text-slate-400 border-brand-border hover:border-white/40 hover:text-white'
                                    }`}
                                >
                                    {f.label}
                                    <span className={`ml-2 ${isActive ? 'text-slate-500' : 'text-slate-600'}`}>
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {isLoading ? (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className="space-y-4">
                                    <Skeleton className="aspect-square bg-brand-surface" />
                                    <Skeleton className="h-6 w-3/4 bg-brand-surface" />
                                    <Skeleton className="h-4 w-1/2 bg-brand-surface" />
                                </div>
                            ))}
                        </div>
                    ) : filteredProducts.length > 0 ? (
                        <div 
                            data-testid="products-grid"
                            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
                        >
                            {filteredProducts.map((product) => (
                                <ProductCard key={product.id} product={product} />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-16">
                            <p className="font-space text-slate-400 text-lg">No products in this category yet.</p>
                            <p className="font-space text-slate-500 mt-2">Check back soon for new drops!</p>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
